import { Env } from '@env';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { format, toZonedTime } from 'date-fns-tz';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import * as React from 'react';
import { Dimensions, ScrollView, View } from 'react-native';
import Modal from 'react-native-modal';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import { useCustomer } from '@/api/customers/use-customer';
import type { Reservation } from '@/api/reservations/types';
import type { CreateReservationPaymentIntent } from '@/api/reservations/use-create-reservation-payment-intent';
import { useCreateReservationPaymentIntent } from '@/api/reservations/use-create-reservation-payment-intent';
import { useReservation } from '@/api/reservations/use-reservation';
import Incidentals from '@/app/(app)/account/trip-history/incidentals';
import Receipt from '@/app/(app)/account/trip-history/receipt';
import Tolls from '@/app/(app)/account/trip-history/tolls';
import { findAdditionalDriver } from '@/components/additional-drivers/status';
import { isDesktop, rgbaToHex, useAuth } from '@/core';
import { usePoll } from '@/core/hooks/use-poll';
import { Button, showErrorMessage, Text } from '@/ui';
import { Chip, ChipType } from '@/ui/chip';
import { Dialog } from '@/ui/dialog';
import { Icon } from '@/ui/icon';
import { ArrowRight } from '@/ui/icons';
import { Dot } from '@/ui/icons/dot';
import { Loader } from '@/ui/loader';

import { NavigationButton, NavigationButtonTypes } from '../navigation-button';

export type TripBreakdownTabOption = 'summary' | 'tolls' | 'incidentals';

const PAGE_WIDTH = Dimensions.get('window').width;

const stripePromise = loadStripe(Env.STRIPE_PUBLISHABLE_KEY);

const formatDate = (d: Date) => {
  if (!d) return;

  const dateFormatted = format(d, 'MM.dd.yyyy');

  return dateFormatted;
};

const StripeWrapper = () => {
  const { theme } = useStyles(stylesheet);
  const [stripeSecret, setStripeSecret] = React.useState<string | null>(null);
  const [customerSessionSecret, setCustomerSessionSecret] = React.useState<
    string | null
  >(null);
  const local = useLocalSearchParams<{ id: string }>();
  const status = useAuth.use.status();

  const {
    mutateAsync: createReservationPaymentIntent,
    isPending: isCreatingReservationPaymentIntent,
  } = useCreateReservationPaymentIntent();

  const {
    data: resData,
    isPending: isFetchingReservation,
    refetch,
    isFetched,
  } = useReservation({
    //@ts-ignore
    variables: { id: local.id },
    enabled: !!local.id,
  });

  React.useEffect(() => {
    if (status === 'signIn') {
      refetch();
    }
  }, [status]);

  const { data: customer } = useCustomer();

  const isAdditionalDriver = !!findAdditionalDriver(
    resData?.additionalDrivers,
    customer
  );

  const needPayment = React.useMemo(() => {
    if (!resData || isAdditionalDriver) return false;

    return resData.outstandingBalance > 0;
  }, [resData, isAdditionalDriver]);

  React.useEffect(() => {
    const handleCreateHoldIntent = async () => {
      try {
        const {
          data: {
            stripeCustomerId,
            ephemeralKey,
            stripeSecret,
            customerSessionSecret,
          },
        }: CreateReservationPaymentIntent =
          await createReservationPaymentIntent({
            id: local.id,
          });

        setStripeSecret(stripeSecret);
        setCustomerSessionSecret(customerSessionSecret);
      } catch (error) {
        console.log(error);
      }
    };

    if (!resData) return;

    if (needPayment && !stripeSecret) {
      handleCreateHoldIntent();
    }
  }, [resData, needPayment]);

  if (isAdditionalDriver) return null;

  if (isFetched && !needPayment) {
    return (
      <Elements
        stripe={stripePromise}
        options={{
          locale: 'en',
          appearance: {
            variables: {
              colorBackground: rgbaToHex(theme.colors.background_2),
            },
            rules: {
              '.Tab': {
                border: 'none',
              },
            },
            theme: UnistylesRuntime.themeName === 'dark' ? 'night' : 'stripe',
          },
        }}
      >
        <TripHistoryBreakdown id={local.id} />
      </Elements>
    );
  }

  if (!stripePromise || !stripeSecret || !customerSessionSecret || !resData) {
    return null;
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        locale: 'en',
        clientSecret: stripeSecret,
        customerSessionClientSecret: customerSessionSecret,
        appearance: {
          variables: {
            colorBackground: rgbaToHex(theme.colors.background_2),
          },
          rules: {
            '.Tab': {
              border: 'none',
            },
          },
          theme: UnistylesRuntime.themeName === 'dark' ? 'night' : 'stripe',
        },
      }}
    >
      <TripHistoryBreakdown id={local.id} stripeSecret={stripeSecret} />
    </Elements>
  );
};

function TripHistoryBreakdown({
  id,
  stripeSecret,
}: {
  id: number;
  stripeSecret?: string;
}) {
  const { theme, styles } = useStyles(stylesheet);
  const router = useRouter();
  const scrollViewRef = React.useRef(null);
  const [activeTab, setActiveTab] =
    React.useState<TripBreakdownTabOption>('summary');
  const stripe = useStripe();
  const elements = useElements();
  const [showPaymentModal, setShowPaymentModal] = React.useState(false);
  const [isFormValid, setIsFormValid] = React.useState(false);
  const [paymentMethodId, setPaymentMethodId] = React.useState<
    string | undefined
  >();
  const [isHandlingPaymentPending, setIsHandlingPaymentPending] =
    React.useState(false);
  const [isHandlingPaymentSuccess, setIsHandlingPaymentSuccess] =
    React.useState(false);
  const [isHandlingPaymentError, setIsHandlingPaymentError] =
    React.useState(false);

  const {
    data: reservation,
    isPending,
    isFetching,
    refetch,
  } = useReservation({
    //@ts-ignore
    variables: { id: id },
  });

  const isBalancePaid = (data: Reservation) => data.outstandingBalance === 0;
  const { poll: pollForBalancePaid } = usePoll(refetch, isBalancePaid);

  const {
    mutateAsync: createReservationPaymentIntent,
    isPending: isCreatingReservationPaymentIntent,
  } = useCreateReservationPaymentIntent();

  const handlePayBalance = async () => {
    if (!stripe || !elements) return;

    try {
      setIsHandlingPaymentPending(true);

      const { error: submitError } = await elements.submit();
      if (submitError) {
        console.log('submit error', submitError);
        setIsHandlingPaymentPending(false);
        setIsHandlingPaymentError(true);
        showErrorMessage('Payment failed. Please try again.');
        return;
      }

      let errorDuringBooking = null;
      if (paymentMethodId !== '-1') {
        const { error } = await stripe.confirmCardPayment(stripeSecret, {
          payment_method: paymentMethodId as string,
        });

        errorDuringBooking = error;
      } else if (paymentMethodId === '-1') {
        try {
          const { error } = await stripe.confirmPayment({
            elements,
            redirect: 'if_required',
          });

          errorDuringBooking = error;
        } catch (e) {
          console.log('crap', e);
        }
      }

      if (errorDuringBooking) {
        console.log('error', errorDuringBooking);
        setIsHandlingPaymentPending(false);
        setIsHandlingPaymentError(true);
        showErrorMessage('Payment failed. Please try again.');
        return;
      }

      setShowPaymentModal(false);

      const latest = await pollForBalancePaid();

      setIsHandlingPaymentPending(false);

      if (latest && isBalancePaid(latest)) {
        setIsHandlingPaymentSuccess(true);
      } else {
        setIsHandlingPaymentError(true);
        showErrorMessage('Payment failed. Please try again.');
      }
    } catch (e) {
      console.error('error', e);
      return;
    }
  };

  const dialog = React.useMemo(() => {
    if (!reservation) return null;

    if (
      reservation.customer.tripsInCollection.length > 0 &&
      !reservation.customer.tripsInCollection.find(
        (trip) => trip.id === reservation.id
      )
    ) {
      return (
        <Dialog
          header={
            <Chip
              type={ChipType.ERROR}
              icon={<Dot />}
              text="Outstanding balance"
              iconWidth={8}
              iconHeight={8}
            />
          }
          body="You have an outstanding balance from a previous rental. You will need to settle the outstanding amount to continue renting. "
          button={
            <Button
              text="Pay Balance"
              type="primarySmall"
              onPress={() => {
                router.push(
                  `/account/trip-history/${reservation.customer.tripsInCollection[0].id}`
                );
              }}
              rightIcon={
                <Icon
                  type="naked"
                  icon={<ArrowRight />}
                  fillColor={theme.colors.button.primary.default.text}
                  width={10}
                  height={10}
                />
              }
              style={{ width: 150 }}
            />
          }
        />
      );
    }

    if (
      reservation.customer.tripsInCollection.length > 0 &&
      reservation.outstandingBalance > 0 &&
      reservation.customer.tripsInCollection.find(
        (trip) => trip.id === reservation.id
      )
    ) {
      return (
        <Dialog
          header={
            <Chip
              type={ChipType.ERROR}
              icon={<Dot />}
              text="Outstanding balance"
              iconWidth={8}
              iconHeight={8}
            />
          }
          body="You have an outstanding balance on this trip. Please settle the amount or contact support for assistance."
          button={
            <Button
              text="Pay Balance"
              type="primarySmall"
              onPress={() => {
                setShowPaymentModal(true);
              }}
              rightIcon={
                <Icon
                  type="naked"
                  icon={<ArrowRight />}
                  fillColor={theme.colors.button.primary.default.text}
                  width={10}
                  height={10}
                />
              }
              style={{ width: 150 }}
            />
          }
        />
      );
    }

    if (['cleaning', 'billable'].includes(reservation.status)) {
      <Dialog
        header={
          <Chip
            type={ChipType.PENDING}
            icon={<Dot />}
            text="Receipt in progress"
            iconWidth={8}
            iconHeight={8}
          />
        }
        body="Final Receipt is in process: Please allow up to 24 hours for the final receipt."
      />;
    }

    if (reservation.status === 'completed') {
      return (
        <Dialog
          header={
            <Chip
              type={ChipType.SUCCESS}
              icon={<Dot />}
              text="Trip is completed"
              iconWidth={8}
              iconHeight={8}
            />
          }
          body="We hope you have enjoyed your trip."
        />
      );
    }
  }, [reservation]);

  React.useEffect(() => {
    scrollViewRef.current?.scrollTo({ y: 0, animated: false });
  }, [activeTab]);

  const handleTabChange = (tab: TripBreakdownTabOption) => {
    if (tab === 'summary') {
      setActiveTab('summary');
    } else if (tab === 'tolls') {
      setActiveTab('tolls');
    } else if (tab === 'incidentals') {
      setActiveTab('incidentals');
    }
  };

  if (!reservation) return null;

  if (!showPaymentModal && isHandlingPaymentPending) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Loader
          title="Processing payment"
          body="This could take a little bit..."
          style={{ width: 200, height: 200 }}
        />
      </View>
    );
  }

  if (isFetching) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Loader style={{ width: 150, height: 150 }} />
      </View>
    );
  }

  if (isDesktop()) {
    return (
      <ScrollView
        contentContainerStyle={{ flexGrow: 1, gap: 32, alignItems: 'center' }}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.container}>
          <View style={{ gap: 16 }}>
            <NavigationButton
              type={NavigationButtonTypes.back_with_text}
              onPress={() => {
                router.back();
              }}
            />

            <Text type="largeTitle">
              {reservation.status === 'completed'
                ? 'Trip Summary'
                : 'Trip Details'}
            </Text>
          </View>

          <View style={styles.tabsContainer}>
            <Button
              tx="tripHistoryBreakdown.summary"
              type={activeTab === 'summary' ? 'primarySmall' : 'secondarySmall'}
              onPress={() => setActiveTab('summary')}
              style={{ flexGrow: 1, flexBasis: 0, minWidth: 0 }}
            />

            <Button
              tx="tripHistoryBreakdown.tolls"
              type={activeTab === 'tolls' ? 'primarySmall' : 'secondarySmall'}
              onPress={() => setActiveTab('tolls')}
              style={{ flexGrow: 1, flexBasis: 0, minWidth: 0 }}
            />

            <Button
              tx="tripHistoryBreakdown.incidentals"
              type={
                activeTab === 'incidentals' ? 'primarySmall' : 'secondarySmall'
              }
              onPress={() => setActiveTab('incidentals')}
              style={{ flexGrow: 1, flexBasis: 0, minWidth: 0 }}
            />
          </View>

          <ScrollView
            contentContainerStyle={styles.scrollContent}
            style={styles.scrollView}
            showsVerticalScrollIndicator={false}
          >
            {dialog}

            {activeTab === 'summary' && (
              <View style={{ gap: 24 }}>
                <Receipt
                  reservation={reservation}
                  onTabChange={handleTabChange}
                />

                {reservation.outstandingBalance > 0 ? (
                  <Button
                    text="Pay Balance"
                    type="primary"
                    onPress={() => {
                      setShowPaymentModal(true);
                    }}
                  />
                ) : null}
              </View>
            )}

            {activeTab === 'tolls' && (
              <View>
                <Tolls reservation={reservation} />
              </View>
            )}

            {activeTab === 'incidentals' && (
              <View>
                <Incidentals reservation={reservation} />
              </View>
            )}
          </ScrollView>
        </View>

        <Modal
          animationIn="slideInUp"
          animationInTiming={300}
          animationOut="slideOutDown"
          animationOutTiming={300}
          isVisible={showPaymentModal}
          onBackdropPress={() => {
            setShowPaymentModal(false);
          }}
          deviceWidth={PAGE_WIDTH}
        >
          <View
            style={{
              backgroundColor: theme.colors.background_2,
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              gap: 24,
              borderRadius: 16,
              padding: 16,
              justifyContent: 'center',
              width: '100%',
              maxWidth: 600,
              alignSelf: 'center',
            }}
          >
            <View
              style={{
                width: '100%',
                alignSelf: 'center',
                marginTop: 16,
                flexDirection: 'row',
                justifyContent: 'center',
                paddingLeft: 16,
                marginRight: 16,
              }}
            >
              <Text type="title2">Pay Balance</Text>

              <NavigationButton
                type={NavigationButtonTypes.cancel}
                onPress={() => {
                  setShowPaymentModal(false);
                }}
                style={{
                  position: 'absolute',
                  right: 0,
                }}
              />
            </View>

            <PaymentElement
              onChange={(e) => {
                setIsFormValid(e.complete);
                setPaymentMethodId(e.value?.payment_method?.id || '-1');
              }}
              options={{
                business: { name: 'Eon' },
                layout: {
                  type: 'accordion',
                  radios: true,
                  spacedAccordionItems: false,
                },
              }}
            />

            <Button
              type="primary"
              text="Pay Balance"
              onPress={handlePayBalance}
              disabled={!isFormValid}
            />
          </View>
        </Modal>
      </ScrollView>
    );
  }

  const headerTitle = reservation
    ? `${formatDate(
        toZonedTime(reservation.startTime, reservation.pickupLocation.timezone)
      )} - ${reservation.pickupLocation.city}`
    : '';

  return (
    <View style={{ flex: 1 }}>
      <Stack.Screen
        options={{
          title: headerTitle,
          headerTitle: headerTitle,
        }}
      />

      {isCreatingReservationPaymentIntent && <Loader />}

      <View style={styles.tabsContainer}>
        <Button
          tx="tripHistoryBreakdown.summary"
          type={activeTab === 'summary' ? 'primarySmall' : 'secondarySmall'}
          onPress={() => setActiveTab('summary')}
          style={{ flexGrow: 1, flexBasis: 0, minWidth: 0 }}
        />

        <Button
          tx="tripHistoryBreakdown.tolls"
          type={activeTab === 'tolls' ? 'primarySmall' : 'secondarySmall'}
          onPress={() => setActiveTab('tolls')}
          style={{ flexGrow: 1, flexBasis: 0, minWidth: 0 }}
        />

        <Button
          tx="tripHistoryBreakdown.incidentals"
          type={activeTab === 'incidentals' ? 'primarySmall' : 'secondarySmall'}
          onPress={() => setActiveTab('incidentals')}
          style={{ flexGrow: 1, flexBasis: 0, minWidth: 0 }}
        />
      </View>

      <ScrollView
        ref={scrollViewRef}
        contentContainerStyle={styles.container}
        showsVerticalScrollIndicator={false}
      >
        {dialog}

        {activeTab === 'summary' && (
          <View style={{ gap: 24 }}>
            <Receipt reservation={reservation} onTabChange={handleTabChange} />

            {reservation.outstandingBalance > 0 ? (
              <Button
                text="Pay Balance"
                type="primary"
                onPress={() => {
                  setShowPaymentModal(true);
                }}
              />
            ) : null}
          </View>
        )}

        {activeTab === 'tolls' && <Tolls reservation={reservation} />}

        {activeTab === 'incidentals' && (
          <Incidentals reservation={reservation} />
        )}
      </ScrollView>

      <Modal
        animationIn="slideInUp"
        animationInTiming={300}
        animationOut="slideOutDown"
        animationOutTiming={300}
        isVisible={showPaymentModal}
        onBackdropPress={() => {
          setShowPaymentModal(false);
        }}
        deviceWidth={PAGE_WIDTH}
      >
        <View
          style={{
            backgroundColor: theme.colors.background_2,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            gap: 24,
            borderRadius: 16,
            padding: 16,
            justifyContent: 'center',
            width: '100%',
            maxWidth: 600,
            alignSelf: 'center',
          }}
        >
          <View
            style={{
              width: '100%',
              alignSelf: 'center',
              marginTop: 16,
              flexDirection: 'row',
              justifyContent: 'center',
              paddingLeft: 16,
              marginRight: 16,
            }}
          >
            <Text type="title2">Authorize Deposit</Text>

            <NavigationButton
              type={NavigationButtonTypes.cancel}
              onPress={() => {
                setShowPaymentModal(false);
              }}
              style={{
                position: 'absolute',
                right: 0,
              }}
            />
          </View>

          <PaymentElement
            onChange={(e) => {
              setIsFormValid(e.complete);
              setPaymentMethodId(e.value?.payment_method?.id || '-1');
            }}
            options={{
              business: { name: 'Eon' },
              layout: {
                type: 'accordion',
                radios: true,
                spacedAccordionItems: false,
              },
            }}
          />

          <Button
            type="primary"
            text={`Pay $${reservation.outstandingBalance}`}
            onPress={handlePayBalance}
            disabled={!isFormValid}
          />
        </View>
      </Modal>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  tabsContainer: {
    flexDirection: 'row',
    paddingTop: 10,
    paddingLeft: {
      xs: 16,
      md: 0,
    },
    paddingRight: 16,
    paddingBottom: 10,
    gap: 8,
    backgroundColor: theme.colors.background_1,
  },
  container: {
    flexGrow: 1,
    paddingTop: 24,
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: 16,
    gap: 16,
    backgroundColor: theme.colors.background_1,
    width: {
      xs: '100%',
      md: 800,
    },
  },
  scrollView: {
    width: '100%',
    height: '100%',
  },
  scrollContent: {
    width: {
      xs: '100%',
      md: 800,
    },
    padding: 16,
    gap: 16,
    alignSelf: 'center',
  },
}));

export default StripeWrapper;
